import React, { useState } from 'react';
import { useListings } from '../../hooks/useAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { ErrorState, EmptyState } from './AnalyticsShared';
import { Search, ChevronLeft, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';

export function ListingsTab({ range }) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('views');
  const [dir, setDir] = useState('desc');

  const { data, isLoading, error, refetch } = useListings({
    range,
    sort,
    dir,
    page,
    pageSize,
    search,
  });

  const handleSort = (column) => {
    if (sort === column) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(column);
      setDir('desc');
    }
    setPage(1);
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return '—';
    return val.toLocaleString();
  };

  const formatPercent = (val) => {
    if (val === null || val === undefined) return '—';
    return `${(val * 100).toFixed(1)}%`;
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '—';
    return `$${(val / 100).toFixed(2)}`;
  };

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search listings by title..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Listings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Listing Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="animate-pulse h-12 bg-gray-100 rounded"></div>
              ))}
            </div>
          ) : !data || !data.listings || data.listings.length === 0 ? (
            <EmptyState
              title="No listings found"
              description={search ? 'Try adjusting your search query.' : 'Create your first listing to see analytics here.'}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('title')}
                      >
                        <div className="flex items-center gap-2">
                          Listing
                          {sort === 'title' && (dir === 'asc' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50 text-right"
                        onClick={() => handleSort('views')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Views
                          {sort === 'views' && (dir === 'asc' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50 text-right"
                        onClick={() => handleSort('clicks')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Clicks
                          {sort === 'clicks' && (dir === 'asc' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50 text-right"
                        onClick={() => handleSort('ctr')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          CTR
                          {sort === 'ctr' && (dir === 'asc' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50 text-right"
                        onClick={() => handleSort('messages')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Messages
                          {sort === 'messages' && (dir === 'asc' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50 text-right"
                        onClick={() => handleSort('orders')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Orders
                          {sort === 'orders' && (dir === 'asc' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50 text-right"
                        onClick={() => handleSort('revenue')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Revenue
                          {sort === 'revenue' && (dir === 'asc' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />)}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.listings.map((listing) => (
                      <TableRow key={listing.listingId}>
                        <TableCell className="font-medium max-w-xs truncate">
                          {listing.title}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(listing.views)}</TableCell>
                        <TableCell className="text-right">{formatNumber(listing.clicks)}</TableCell>
                        <TableCell className="text-right">{formatPercent(listing.ctr)}</TableCell>
                        <TableCell className="text-right">{formatNumber(listing.messages || 0)}</TableCell>
                        <TableCell className="text-right">{formatNumber(listing.orders)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(listing.gmv_cents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data.total_pages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(page * pageSize, data.total)}</span> of{' '}
                    <span className="font-medium">{data.total}</span> listings
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {data.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                      disabled={page === data.total_pages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
